import { Flex, Table } from 'antd'
import { useState } from 'react'

import {
  usePatientTreatmentServiceGetPatientPregnancyMonitoringEndpoint,
  usePatientTreatmentServiceGetPatientPregnancyMonitoringListEndpoint
} from 'src/api/queries'
import {
  ClassifierCategory,
  ClassifierType,
  PregnancyMonitoringSortBy2
} from 'src/api/requests'
import {
  ClassifierInfiniteSelect,
  PageBreadcrumbs,
  Select,
  PersonPregnancyDetailsModal
} from 'src/components'
import {
  usePregnancyColumns,
  usePregnancyConfig
} from 'src/features/persons/components'
import { useClassifierId, useTablePagination } from 'src/hooks'
import { useLocales } from 'src/locales'
import {
  getOrderingDirection,
  normalizeOrdering,
  resetPagination
} from 'src/utils'

export const PersonPregnancyMonitoringPage = () => {
  const { t } = useLocales()
  const { options } = usePregnancyConfig()

  const [pregnancyId, setPregnancyId] = useState<string | null>(null)
  const { columns } = usePregnancyColumns({
    onOpenDetails: (id) => {
      setPregnancyId(id)
    }
  })

  const pagination = useTablePagination()

  const [ordering, setOrdering] = useState('')
  const [pregnancyTypeId, setPregnancyTypeId] = useState('')

  const classifierPregnancyTypeId = useClassifierId(
    ClassifierType.PREGNANCY_TYPE,
    ClassifierCategory.TREATMENT
  )

  const queryParams = {
    pageNumber: pagination.current,
    pageSize: pagination.pageSize,
    ...(ordering && {
      sortBy: normalizeOrdering(ordering) as PregnancyMonitoringSortBy2,
      sortDirection: getOrderingDirection(ordering)
    }),
    ...(pregnancyTypeId && { pregnancyTypeId })
  }

  const { data, isLoading } =
    usePatientTreatmentServiceGetPatientPregnancyMonitoringListEndpoint(
      queryParams
    )

  const onSelect = (val: string) => {
    setOrdering(val)
    resetPagination(pagination)
  }

  const onPregnancySelect = (val: string) => {
    setPregnancyTypeId(val)
    resetPagination(pagination)
  }

  return (
    <PageBreadcrumbs
      items={[{ title: t('persons.person_pregnancy') }]}
      title={t('persons.person_pregnancy')}
    >
      <Flex vertical gap={16}>
        <Flex gap={8}>
          <ClassifierInfiniteSelect
            hasDefault
            onSelect={onPregnancySelect}
            onClear={() => setPregnancyTypeId('')}
            id={classifierPregnancyTypeId}
            placeholder={t('pregnancy.pregnancy_type')}
            style={{
              minWidth: 200
            }}
          />

          <Select onSelect={onSelect} options={options} allowClear />
        </Flex>

        <Table
          rowKey="id"
          scroll={{ x: 'max-content' }}
          loading={isLoading}
          columns={columns}
          dataSource={data?.data}
          pagination={{ ...pagination, total: data?.totalCount }}
        />

        {pregnancyId && (
          <PersonPregnancyDetailsModal
            onCancel={() => setPregnancyId(null)}
            useQueryFn={
              usePatientTreatmentServiceGetPatientPregnancyMonitoringEndpoint
            }
            id={pregnancyId}
          />
        )}
      </Flex>
    </PageBreadcrumbs>
  )
}
